import type { Rule } from '@tunasong/schemas'
import { findAction, isKnownEntityType } from './rule-vocabulary.js'

/**
 * Why a rule cannot be enabled yet, or `undefined` when it is ready.
 *
 * An unrecognised command — one a newer client wrote — cannot be judged
 * complete or incomplete, but it is also not safe to enable: `RuleEditor`
 * cannot render what it does other than its raw id, so a user could not
 * knowingly turn it on. Per the "no impossible or non-functional rules"
 * product principle, staying disabled beats letting someone switch on a
 * rule this client cannot honestly describe.
 */
export const describeIncompleteness = (rule: Rule): string | undefined => {
  const action = rule.actions[0]
  if (!action) {
    return 'Pick an action first.'
  }

  /*
   * A type condition holding a value outside the curated list this client
   * offers — e.g. a rule saved before the list was curated — is the same
   * "cannot honestly describe it" problem as an unrecognised command below,
   * so it gets the same treatment.
   */
  const typeCondition = rule.conditions.find(condition => condition.kind === 'type')
  if (typeCondition && typeCondition.kind === 'type' && !isKnownEntityType(typeCondition.entityType)) {
    return 'This app doesn’t support this entity type yet.'
  }

  /*
   * `condition.tag === ''` / `condition.spaceId === ''` are the native select's
   * and the freshly-added-condition's "nothing picked yet" values. The engine
   * ANDs every condition (`evaluate-conditions.ts`), and `entity.tags?.includes('')`
   * and `entity.parentId === ''` are always false — so a rule holding one of these
   * would save clean and enable clean, yet could never fire. Catching it here is
   * what keeps that from being a silent, two-click dead end.
   */
  for (const condition of rule.conditions) {
    if (condition.kind === 'tag' && condition.tag === '') {
      return 'Finish the tag first.'
    }
    if (condition.kind === 'space' && condition.spaceId === '') {
      return 'Finish the space first.'
    }
  }

  const spec = findAction(action.commandId)
  if (!spec) {
    return 'This app doesn’t recognise this action yet.'
  }
  if (!spec.arg) {
    return undefined
  }

  /*
   * Three distinct cases: `undefined` (key absent), `''` (select empty value),
   * and other values (chosen). `null` is chosen — for `move-entity` it means
   * the Inbox. Empty string is the native select's "nothing picked" value.
   * We treat both as incomplete; testing truthiness would lose the `null` case.
   */
  const argValue = action.args[spec.arg.name]
  const isChosen = argValue !== undefined && argValue !== ''
  return isChosen ? undefined : spec.arg.missingLabel
}
